// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import {
  Alert, AlertColor, IconButton, Snackbar
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

/**
 * Props for the {@link Notification} component
 */
export interface NotificationProps {
  /** The condition to which this notification will open */
  open: boolean;
  /** What to do when this notification is closed */
  onClose: (() => void) | undefined;
  /** The severity color of this notification */
  severity: AlertColor;
  /** The message to display on this notification */
  message: string;
  /** The data-testid attached to this notification component */
  'data-testid': string;
  /** Number of seconds to dismiss the notification */
  autoHideSeconds?: number
}

/**
 * Component to display an Snackbar Alert notification.
 * The notification appears at the bottom center of the screen and can be closed manually or automatically after a specified duration.
 *
 * @example
 * ```tsx
 * <Notification
 *   open={true}
 *   onClose={() => setOpen(false)}
 *   severity="success"
 *   message="Operation completed successfully!"
 *   data-testid="notification-success"
 *   autoHideSeconds={3}
 * />
 * ```
 */
export default function Notification(
  {
    open,
    onClose,
    severity,
    message,
    'data-testid': testId,
    autoHideSeconds
  }: Readonly<NotificationProps>
): JSX.Element {
  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={open}
      onClose={onClose}
      autoHideDuration={1000 * (autoHideSeconds ?? 5)}
      action={(
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      data-testid={testId}
    >
      <Alert
        variant="filled"
        onClose={onClose}
        severity={severity}
        sx={{
          width: '50vw',
          borderRadius: 0,
          boxShadow:
            '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.12)'
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  )
}

Notification.defaultProps = {
  autoHideSeconds: 5
}
